import React, { useCallback } from 'react';
import { ChipSelector } from './ChipSelector';
import DayFirstDate from "./DayFirstDate";

// ── Rating options ─────────────────────────────────────────────────
const RATING_OPTIONS = [
  { value: '0', label: 'Not at all' },
  { value: '1', label: 'A little' },
  { value: '2', label: 'Moderately' },
  { value: '3', label: 'A lot' },
  { value: '4', label: 'Extremely' },
];

// ── Styles ─────────────────────────────────────────────────────────
const sectionStyle = { marginBottom: 32, paddingBottom: 16, borderBottom: '1px solid #e5e7eb' };
const sectionTitle = { fontSize: '1.25rem', fontWeight: 700, color: '#1e3a5f', marginBottom: 4 };
const sectionSub = { fontSize: '0.8125rem', color: '#6b7280', marginBottom: 16 };
const fieldGap = { marginBottom: 16 };
const labelStyle = { fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };
const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
  fontSize: '0.875rem', fontFamily: 'inherit', color: '#1a202c',
};
const textareaStyle = { ...inputStyle, minHeight: 80, resize: 'vertical' };

const pairCard = {
  padding: '14px 16px', borderRadius: 10, border: '1px solid #e5e7eb',
  backgroundColor: '#fafafa', marginBottom: 10,
};
const questionText = {
  fontSize: '0.8125rem', color: '#1f2937', lineHeight: 1.5, marginBottom: 10, fontWeight: 500,
};
const ratingRow = {
  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
};
const periodLabel = {
  fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.03em',
  padding: '2px 8px', borderRadius: 4, flexShrink: 0, whiteSpace: 'nowrap',
};

// ── Item definitions ───────────────────────────────────────────────
const ITEMS = [
  { section: 'Social Interaction — Interactions', pairs: [
    { child: 7, recent: 8, text: 'interactions focused on their needs (rather than socially motivated) and/or were they unusually withdrawn or passive in social situations' },
    { child: 9, recent: 10, text: 'initiate social interactions in an inappropriate way and/or respond inappropriately to the social approaches of others' },
    { child: 11, recent: 12, text: 'avoid or fail to engage appropriately in two-way conversations for social purposes' },
    { child: 13, recent: 14, text: 'struggle to, or fail to, spontaneously share their feelings and interests with others' },
  ]},
  { section: 'Social Interaction — Communication', pairs: [
    { child: 15, recent: 16, text: 'fail to use and integrate both verbal and non-verbal methods of communication' },
    { child: 17, recent: 18, text: 'show a limited range of feelings through facial expressions and body language, or use them in unusual ways' },
  ]},
  { section: 'Social Interaction — Relationships', pairs: [
    { child: 19, recent: 20, text: 'struggle, or show no interest, in making and maintaining friendships without the assistance of parents/caregivers' },
    { child: 21, recent: 22, text: 'avoid, or struggle to engage co-operatively in imaginative play with other children / games or activities with others' },
    { child: 23, recent: 24, text: 'be unable to moderate their behaviour, leading them to behave inappropriately when interacting with other people' },
  ]},
  { section: 'Repetitive Behaviours', pairs: [
    { child: 25, recent: 26, text: 'engage repeatedly in any odd body movements, behaviours or mannerisms (e.g. flapping hands, spinning, tapping)' },
    { child: 27, recent: 28, text: 'interact repeatedly with objects in a manner that was excessive or unnecessary (e.g. spinning wheels, lining up objects)' },
    { child: 29, recent: 30, text: 'engage in a repetitive speech pattern (e.g. repeating words, sentences, odd phrases)' },
  ]},
  { section: 'Routines and Interests', pairs: [
    { child: 31, recent: 32, text: 'insist that routines are rigidly followed and/or show a marked negative reaction to changes in routine' },
    { child: 33, recent: 34, text: 'repeatedly perform patterns of behaviours (e.g. verbal or non-verbal compulsive rituals)' },
    { child: 35, recent: 36, text: 'be fixated on or preoccupied with unusual objects that seemed odd to others' },
    { child: 37, recent: 38, text: 'go through phases of 3 months or more where they had a special interest or hobby that dominated their focus' },
  ]},
  { section: 'Sensory Experiences', pairs: [
    { child: 39, recent: 40, text: 'show an unusual response to pain or changes in temperature' },
    { child: 41, recent: 42, text: 'show an intense fascination or aversion for certain ordinary sounds, feelings, sights' },
    { child: 43, recent: 44, text: 'show an intense fascination or aversion for the texture, taste and/or smell of food or objects' },
    { child: 45, recent: 46, text: 'show an intense fascination or aversion to lights or movement' },
  ]},
];

// ── Component ──────────────────────────────────────────────────────
export default function DASIInformantForm({ responses, setResponses }) {
  const r = responses;
  const set = useCallback((key, val) => setResponses(prev => ({ ...prev, [key]: val })), [setResponses]);

  return (
    <div style={{ maxWidth: 800 }}>

      {/* ─── Demographics ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>About You and the Person You Are Completing This For</h2>
        <div style={{ display: 'flex', gap: 16, ...fieldGap }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Date of completion</label>
            <DayFirstDate label="Date" inputStyle={inputStyle} value={r.completion_date || ''} onChange={v => set('completion_date', v)} testId="dasi-ir-date" />
          </div>
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Name of person you are completing this assessment about</label>
          <input type="text" style={inputStyle} value={r.subject_name || ''} onChange={e => set('subject_name', e.target.value)} data-testid="dasi-ir-subject" />
        </div>
        <div style={{ display: 'flex', gap: 16, ...fieldGap }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Date of birth</label>
            <DayFirstDate label="Date of birth" notFuture inputStyle={inputStyle} value={r.subject_dob || ''} onChange={v => set('subject_dob', v)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>How long have you known them?</label>
            <input type="text" style={inputStyle} value={r.known_duration || ''} onChange={e => set('known_duration', e.target.value)} placeholder="e.g. since birth, 5 years" />
          </div>
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Your relationship to them</label>
          <input type="text" style={inputStyle} value={r.relationship || ''} onChange={e => set('relationship', e.target.value)} placeholder="e.g. Parent, Carer, Sibling" />
        </div>
      </div>

      {/* ─── Rating Sections ─── */}
      {ITEMS.map((group, gi) => {
        const firstName = (r.subject_name || '').split(' ')[0].trim();
        const leadIn = firstName ? `Did ${firstName}...` : 'Did your child...';
        return (
        <div key={gi} style={sectionStyle}>
          <h2 style={sectionTitle}>{group.section}</h2>
          <p style={sectionSub}>
            For each question, rate how much this applied <strong>as a child</strong> and <strong>recently</strong>.
          </p>
          <p style={{ fontSize: '1rem', fontWeight: 600, color: '#1e3a5f', marginBottom: 12 }}>{leadIn}</p>
          {group.pairs.map((pair, pi) => (
            <div key={pi} style={pairCard}>
              <p style={questionText}>...{pair.text}</p>
              <div style={ratingRow}>
                <span style={{ ...periodLabel, backgroundColor: '#dbeafe', color: '#1e40af' }}>As a child</span>
                <div style={{ flex: 1 }}>
                  <ChipSelector options={RATING_OPTIONS} value={r[`item_${pair.child}`] || ''} onChange={v => set(`item_${pair.child}`, v)} />
                </div>
              </div>
              <div style={ratingRow}>
                <span style={{ ...periodLabel, backgroundColor: '#fef3c7', color: '#92400e' }}>Recently (past 6 months)</span>
                <div style={{ flex: 1 }}>
                  <ChipSelector options={RATING_OPTIONS} value={r[`item_${pair.recent}`] || ''} onChange={v => set(`item_${pair.recent}`, v)} />
                </div>
              </div>
              <textarea style={{ ...textareaStyle, marginTop: 8, minHeight: 40, fontSize: '0.85rem' }} value={r[`item_${pair.child}_examples`] || ''} onChange={e => set(`item_${pair.child}_examples`, e.target.value)} placeholder="Any examples you think would be helpful for us to know (optional)" />
            </div>
          ))}
        </div>
        );
      })}

      {/* ─── Summary ─── */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={sectionTitle}>Summary</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>At what age did you first notice these symptoms or problems?</label>
          <input type="text" style={inputStyle} value={r.onset_age || ''} onChange={e => set('onset_age', e.target.value)} placeholder="e.g. around age 2-3" data-testid="dasi-ir-onset" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Have these symptoms been present and caused difficulties over the past six months?</label>
          <textarea style={textareaStyle} value={r.duration_impact || ''} onChange={e => set('duration_impact', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>How have these symptoms impacted on academic, occupational and/or social functioning?</label>
          <textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.functional_impact || ''} onChange={e => set('functional_impact', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Is there anything else you feel we should know or you would like to tell us?</label>
          <textarea style={{ ...textareaStyle, minHeight: 80 }} value={r.anything_else || ''} onChange={e => set('anything_else', e.target.value)} />
        </div>
      </div>
    </div>
  );
}
